import React from 'react';

import { formatDate } from '@/utils';
import { useHouseholdStatisticsForBenchmarks } from 'views/portfolio/reporting-queries';

import { RelativeStatisticsCard } from './RelativeStatisticsCard';
import { RiskMeasurementsCard } from './RiskMeasurementsCard';
import { TrailingReturnsCard } from './TrailingReturnsCard';

interface AdvancedAnalyticsProps {
    householdGUID: string;
    selectedAccountGUIDs: string[];
    includeClosed: boolean;
    // The header's benchmarks with their chart colors. The first is the primary benchmark of the risk
    // and relative statistics; Trailing returns gets a row for each. Empty while benchmarks load or
    // when there are none.
    benchmarks: { name: string; color: string }[];
}

// The scatter labels the household's point; the reporting scope has no display name for
// the household, so a generic label stands in for the design's portfolio name.
const ACCOUNT_POINT_LABEL = 'Household portfolio';

// The Advanced Analytics section of the performance report: trailing returns against every header
// benchmark, and the risk measurements and relative statistics against the primary one (the design
// compares those one benchmark at a time). Statistics windows are anchored server-side, independent
// of the page-level date range (per the design's disclosure).
export const AdvancedAnalytics = ({
    householdGUID,
    selectedAccountGUIDs,
    includeClosed,
    benchmarks,
}: AdvancedAnalyticsProps) => {
    const statisticsQueries = useHouseholdStatisticsForBenchmarks({
        householdGUID,
        groupByGuids: selectedAccountGUIDs,
        includeClosed,
        benchmarkNames: benchmarks.map((benchmark) => benchmark.name),
    });
    // A benchmark's Trailing returns row appears once its statistics have loaded.
    const comparisons = benchmarks.flatMap((benchmark, index) => {
        const view = statisticsQueries[index]?.data;
        return view ? [{ ...benchmark, view }] : [];
    });

    // No benchmark (none available, or still loading) — nothing to compare against, so no section.
    const [primary] = benchmarks;
    const [primaryQuery] = statisticsQueries;
    if (!primary || !primaryQuery) {
        return null;
    }
    const { data: view, isPending, isError, refetch } = primaryQuery;

    // gap-6 between cards, as in every reporting Section.
    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-4">
                {/* The other reporting cards' title style (e.g. NavChart's "Market Value"). */}
                <h2 className="font-heading text-cocoa-1000 text-xl whitespace-nowrap">Advanced Analytics</h2>
                <div className="bg-border h-px flex-1" />
                <span className="text-cocoa-400 text-xs whitespace-nowrap">
                    Benchmark &amp; risk-adjusted statistics
                </span>
            </div>

            <div className="flex items-center gap-3">
                {/* Statistics anchor at the last complete calendar month, which can trail
                    today's quotes — the anchor is surfaced so to-date figures aren't read
                    as "through today". */}
                {view && (
                    <span className="text-cocoa-400 ml-auto text-xs whitespace-nowrap">
                        As of {formatDate(view.asOfDate, 'HUMAN')}
                    </span>
                )}
            </div>

            {isPending ? (
                <div className="bg-cocoa-100 h-64 w-full animate-pulse rounded-md" />
            ) : isError ? (
                <div className="text-cocoa-400 border-border rounded-2xl border bg-white px-4 py-8 text-center text-sm">
                    Statistics could not be loaded.{' '}
                    <button type="button" className="text-cocoa-500 underline" onClick={() => refetch()}>
                        Try again
                    </button>
                </div>
            ) : view?.windows.length ? (
                <>
                    {view.trailingReturns.length > 0 && (
                        <TrailingReturnsCard view={view} comparisons={comparisons} accountLabel={ACCOUNT_POINT_LABEL} />
                    )}
                    <RiskMeasurementsCard
                        view={view}
                        benchmarkColor={primary.color}
                        accountLabel={ACCOUNT_POINT_LABEL}
                    />
                    <RelativeStatisticsCard view={view} />
                </>
            ) : (
                <div className="text-cocoa-400 border-border rounded-2xl border bg-white px-4 py-8 text-center text-sm">
                    Not enough overlapping history with {primary.name} to compute statistics. At least two full calendar
                    months of both series are required.
                </div>
            )}

            <p className="text-cocoa-400 max-w-4xl text-xs leading-relaxed">
                Performance figures are time-weighted and net of advisory fees unless otherwise noted. Trailing returns
                and risk and relative statistics are computed from monthly returns against the selected benchmark,
                through the most recent complete calendar month (the as-of date above) and independent of the page-level
                date range. Quarter- and year-to-date figures exclude the month in progress and may differ from quotes
                published through the latest trading day. Past performance is not indicative of future results.
            </p>
        </div>
    );
};
